import { getQuantileValue } from '../../components/HeatMapChart/utils';
import type { HeatmapWidgetAxisParams } from '../types';

import { getAxisBinCount, getAxisBinIndex, isValidHeatmapGrid } from './heatmapGrid';
import type { HeatmapDataPoint, HeatmapBin, HeatmapBinIndex, HeatmapBinningResult } from './types';

type Bin = HeatmapBin & {
  ropQuantiles: Map<number, number>;
  mseQuantiles: Map<number, number>;
  lastRopDepth: number;
  lastMseDepth: number;
};

export class HeatmapBinner {
  private readonly xBinCount: number;

  private readonly yBinCount: number;

  private readonly bins: Bin[];

  private readonly ropRange = { min: Infinity, max: -Infinity };

  private readonly mseRange = { min: Infinity, max: -Infinity };

  private hasRop = false;

  private hasMse = false;

  constructor(
    private readonly xParams: HeatmapWidgetAxisParams,
    private readonly yParams: HeatmapWidgetAxisParams,
  ) {
    const valid = isValidHeatmapGrid(xParams, yParams);
    this.xBinCount = valid ? getAxisBinCount(xParams) : 0;
    this.yBinCount = valid ? getAxisBinCount(yParams) : 0;

    this.bins = Array.from({ length: this.xBinCount * this.yBinCount }, (_, index) => ({
      xIndex: index % this.xBinCount,
      yIndex: Math.floor(index / this.xBinCount),
      ropValues: [],
      mseValues: [],
      pointsInBin: 0,
      ropQuantiles: new Map(),
      mseQuantiles: new Map(),
      lastRopDepth: -Infinity,
      lastMseDepth: -Infinity,
    }));
  }

  addPoint(x: number, y: number, rop: number | null, mse: number | null, depth = -Infinity): HeatmapBinIndex | null {
    const xIndex = getAxisBinIndex(x, this.xParams, this.xBinCount);
    const yIndex = getAxisBinIndex(y, this.yParams, this.yBinCount);
    if (!this.bins.length || xIndex < 0 || yIndex < 0) {
      return null;
    }
    const binIndex = yIndex * this.xBinCount + xIndex;
    const bin = this.bins[binIndex];

    if (rop !== null && Number.isFinite(rop)) {
      bin.pointsInBin += 1;
      bin.ropValues.push(rop);
      bin.ropQuantiles.clear();
      bin.lastRopDepth = Math.max(bin.lastRopDepth, depth);
      this.updateRopRange(rop);
    }

    if (mse !== null && Number.isFinite(mse)) {
      bin.mseValues.push(mse);
      bin.mseQuantiles.clear();
      bin.lastMseDepth = Math.max(bin.lastMseDepth, depth);
      this.updateMseRange(mse);
    }

    return { xIndex, yIndex };
  }

  getActiveBins(startDepth: number): Pick<HeatmapBinningResult, 'activeRopBins' | 'activeMseBins'> {
    const activeRopBins: HeatmapBinIndex[] = [];
    const activeMseBins: HeatmapBinIndex[] = [];
    for (const bin of this.bins) {
      const index = { xIndex: bin.xIndex, yIndex: bin.yIndex };
      if (Number.isFinite(bin.lastRopDepth) && bin.lastRopDepth >= startDepth) {
        activeRopBins.push(index);
      }
      if (Number.isFinite(bin.lastMseDepth) && bin.lastMseDepth >= startDepth) {
        activeMseBins.push(index);
      }
    }
    return { activeRopBins, activeMseBins };
  }

  toResult(params: { ropAnalysisType: number; mseAnalysisType: number }): HeatmapBinningResult {
    const data: HeatmapDataPoint[] = [];

    for (let y = 0; y < this.yBinCount; y += 1) {
      for (let x = 0; x < this.xBinCount; x += 1) {
        const index = y * this.xBinCount + x;
        const bin = this.bins[index];

        if (!bin.ropValues.length && !bin.mseValues.length) {
          continue;
        }

        data.push({
          xIndex: x,
          yIndex: y,
          ropQuantile: bin.ropValues.length
            ? this.getQuantile(bin.ropValues, bin.ropQuantiles, params.ropAnalysisType)
            : null,
          mseQuantile: bin.mseValues.length
            ? this.getQuantile(bin.mseValues, bin.mseQuantiles, params.mseAnalysisType)
            : null,
          pointsInBin: bin.pointsInBin,
        });
      }
    }

    return {
      data,
      ropRange: this.hasRop ? { ...this.ropRange } : null,
      mseRange: this.hasMse ? { ...this.mseRange } : null,
      activeRopBins: [],
      activeMseBins: [],
    };
  }

  private getQuantile(values: number[], cache: Map<number, number>, percentile: number) {
    const cached = cache.get(percentile);
    if (cached !== undefined) {
      return cached;
    }
    const value = getQuantileValue(values, percentile);
    cache.set(percentile, value);
    return value;
  }

  private updateRopRange(value: number) {
    this.hasRop = true;
    if (value < this.ropRange.min) {
      this.ropRange.min = value;
    }
    if (value > this.ropRange.max) {
      this.ropRange.max = value;
    }
  }

  private updateMseRange(value: number) {
    this.hasMse = true;
    if (value < this.mseRange.min) {
      this.mseRange.min = value;
    }
    if (value > this.mseRange.max) {
      this.mseRange.max = value;
    }
  }
}
